/**
 * App API service.
 * Provides CRUD operations and lifecycle management for apps.
 * Apps are sandboxes with the type=app label.
 *
 * @module services/appService
 */

import { httpClient } from './httpClient';
import type {
  SandboxRequest,
  SandboxResponse,
  SnapshotRequest,
  AppAddPortRequest,
  RemovePortRequest,
  UpdatePortsRequest,
  SandboxPort,
  EgressPolicy,
  EgressDecisionsResponse,
} from '@/types';

const ENDPOINT = '/sandboxes';

/** The label key/value that identifies an app */
const APP_TYPE_LABEL = { key: 'type', value: 'app' };

/**
 * App Service
 * Encapsulates all app API operations (apps are sandboxes with type=app label)
 */
export const appService = {
  /**
   * Get total count of apps (sandboxes with type=app label)
   * @returns Promise<number> - Total count
   */
  async getCount(): Promise<number> {
    console.log('[AppService] Fetching apps count...');
    try {
      // Fetch sandboxes and filter by type=app label
      // Note: In a real implementation, this should be a server-side filter
      const response = await httpClient.get<SandboxResponse[]>(`${ENDPOINT}?page=1&pageSize=100`);
      const apps = response.data.filter(
        (s) => s.labels && s.labels[APP_TYPE_LABEL.key] === APP_TYPE_LABEL.value
      );
      console.log('[AppService] Total apps:', apps.length);
      return apps.length;
    } catch (error) {
      console.error('[AppService] Failed to fetch apps count:', error);
      throw error;
    }
  },

  /**
   * List apps with pagination (sandboxes with type=app label)
   * @param page - Page number (1-based, default: 1)
   * @param pageSize - Number of items per page (default: 25, max: 100)
   * @returns Promise<SandboxResponse[]> - Array of apps
   */
  async list(page: number = 1, pageSize: number = 25): Promise<SandboxResponse[]> {
    console.log('[AppService] Fetching apps - Page:', page, 'PageSize:', pageSize);
    try {
      // Fetch sandboxes with requested pagination and filter by type=app label
      // Note: In a real implementation, this should be a server-side filter
      const response = await httpClient.get<SandboxResponse[]>(
        `${ENDPOINT}?page=${page}&pageSize=${pageSize}`
      );
      const apps = response.data.filter(
        (s) => s.labels && s.labels[APP_TYPE_LABEL.key] === APP_TYPE_LABEL.value
      );
      console.log('[AppService] Fetched', apps.length, 'apps');
      return apps;
    } catch (error) {
      console.error('[AppService] Failed to fetch apps:', error);
      throw error;
    }
  },

  /**
   * Get an app by ID
   * @param id - The app ID
   * @returns Promise<SandboxResponse> - The app
   */
  async getById(id: string): Promise<SandboxResponse> {
    console.log('[AppService] Fetching app:', id);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.get<SandboxResponse>(`${ENDPOINT}/${encodedId}`);
      console.log('[AppService] Fetched app:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[AppService] Failed to fetch app:', id, error);
      throw error;
    }
  },

  /**
   * Create a new app (sandbox with type=app label)
   * @param request - The sandbox request payload (type=app label will be added)
   * @returns Promise<SandboxResponse> - The created app with port 80 enabled
   */
  async create(request: SandboxRequest): Promise<SandboxResponse> {
    const diskImageId = request.sourcesRef?.diskImage?.id;
    console.log('[AppService] Creating app with disk image:', diskImageId);

    // Ensure the type=app label is set
    const appRequest: SandboxRequest = {
      ...request,
      labels: {
        ...request.labels,
        [APP_TYPE_LABEL.key]: APP_TYPE_LABEL.value,
      },
    };

    console.log('[AppService] Request payload:', JSON.stringify(appRequest, null, 2));
    try {
      // Pass includeDebug=true to get performance traces for waterfall visualization
      const response = await httpClient.put<SandboxResponse>(
        `${ENDPOINT}?includeDebug=true`,
        appRequest
      );
      console.log('[AppService] Created app:', response.data.id);

      // Automatically add port 80 for apps (required for apps domain routing)
      try {
        console.log('[AppService] Adding port 80 to app:', response.data.id);
        await this.addPort(response.data.id, {
          port: 80,
          name: 'http',
          activationMode: 'OnDemand',
        });
        console.log('[AppService] Port 80 added successfully');
      } catch (portError) {
        // Log but don't fail - the app is created, port can be added later
        console.warn('[AppService] Failed to add port 80, continuing anyway:', portError);
      }

      // Re-fetch the app to get updated state with port 80
      return await this.getById(response.data.id);
    } catch (error: unknown) {
      console.error('[AppService] Failed to create app:', error);
      const axiosError = error as {
        message?: string;
        response?: { data?: unknown; status?: number };
      };
      console.error('[AppService] Error details:', {
        message: axiosError.message,
        response: axiosError.response?.data,
        status: axiosError.response?.status,
      });
      throw error;
    }
  },

  /**
   * Delete an app by ID
   * @param id - The app ID to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    console.log('[AppService] Deleting app:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedId}`);
      console.log('[AppService] Deleted app:', id);
    } catch (error) {
      console.error('[AppService] Failed to delete app:', id, error);
      throw error;
    }
  },

  /**
   * Resume a stopped app
   * @param id - The app ID to resume
   * @returns Promise<void>
   */
  async resume(id: string): Promise<void> {
    console.log('[AppService] Resuming app:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.post(`${ENDPOINT}/${encodedId}/resume`);
      console.log('[AppService] Resumed app:', id);
    } catch (error) {
      console.error('[AppService] Failed to resume app:', id, error);
      throw error;
    }
  },

  /**
   * Stop a running app
   * @param id - The app ID to stop
   * @returns Promise<void>
   */
  async stop(id: string): Promise<void> {
    console.log('[AppService] Stopping app:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.post(`${ENDPOINT}/${encodedId}/stop`);
      console.log('[AppService] Stopped app:', id);
    } catch (error) {
      console.error('[AppService] Failed to stop app:', id, error);
      throw error;
    }
  },

  /**
   * Create a snapshot of an app
   * @param id - The app ID to snapshot
   * @param request - Optional snapshot request payload (labels)
   * @returns Promise<{ snapshotId: string }> - The created snapshot ID
   */
  async createSnapshot(id: string, request?: SnapshotRequest): Promise<{ snapshotId: string }> {
    console.log('[AppService] Creating snapshot for app:', id);
    const encodedId = encodeURIComponent(id);
    const body: SnapshotRequest = request ?? { labels: {} };

    try {
      const response = await httpClient.post<{ id: string }>(
        `${ENDPOINT}/${encodedId}/snapshot`,
        body
      );
      console.log('[AppService] Created snapshot:', response.data.id);
      return { snapshotId: response.data.id };
    } catch (error) {
      console.error('[AppService] Failed to create snapshot for app:', id, error);
      throw error;
    }
  },

  /**
   * Execute a command in an app
   * @param id - The app ID
   * @param command - The command to execute
   * @param workingDirectory - Optional working directory
   * @returns Promise<ExecuteCommandResponse> - The command execution result
   */
  async executeCommand(
    id: string,
    command: string,
    workingDirectory?: string
  ): Promise<import('@/types').ExecuteCommandResponse> {
    console.log('[AppService] Executing command in app:', id, 'Command:', command);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<import('@/types').ExecuteCommandResponse>(
        `${ENDPOINT}/${encodedId}/executeShellCommand`,
        { command, workingDirectory }
      );
      console.log('[AppService] Command executed. Exit code:', response.data.exitCode);
      return response.data;
    } catch (error) {
      console.error('[AppService] Failed to execute command in app:', id, error);
      throw error;
    }
  },

  /**
   * Add a port to an app with app-specific options (activationMode, auth)
   * @param id - The app ID
   * @param request - The port to add with app-specific options
   * @returns Promise<SandboxPort> - The added port with URL
   */
  async addPort(id: string, request: AppAddPortRequest): Promise<SandboxPort> {
    console.log('[AppService] Adding port to app:', id, 'Port:', request.port);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<SandboxPort>(
        `${ENDPOINT}/${encodedId}/ports/add`,
        request
      );
      console.log('[AppService] Added port:', response.data);
      return response.data;
    } catch (error) {
      console.error('[AppService] Failed to add port to app:', id, error);
      throw error;
    }
  },

  /**
   * Remove a port from an app
   * @param id - The app ID
   * @param request - The port to remove (by name or port number)
   * @returns Promise<void>
   */
  async removePort(id: string, request: RemovePortRequest): Promise<void> {
    console.log('[AppService] Removing port from app:', id, 'Request:', request);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.post(`${ENDPOINT}/${encodedId}/ports/remove`, request);
      console.log('[AppService] Removed port from app:', id);
    } catch (error) {
      console.error('[AppService] Failed to remove port from app:', id, error);
      throw error;
    }
  },

  /**
   * Update all ports on an app
   * @param id - The app ID
   * @param request - The ports to set
   * @returns Promise<SandboxPort[]> - The updated ports
   */
  async updatePorts(id: string, request: UpdatePortsRequest): Promise<SandboxPort[]> {
    console.log('[AppService] Updating ports on app:', id, 'Ports:', request.ports);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.put<SandboxPort[]>(
        `${ENDPOINT}/${encodedId}/ports`,
        request
      );
      console.log('[AppService] Updated ports:', response.data);
      return response.data;
    } catch (error) {
      console.error('[AppService] Failed to update ports on app:', id, error);
      throw error;
    }
  },

  /**
   * Set the egress policy for a running app.
   * POST /sandboxes/:id/egresspolicy
   */
  async setEgressPolicy(id: string, policy: EgressPolicy): Promise<EgressPolicy> {
    console.log('[AppService] Setting egress policy for app:', id, 'Policy:', policy);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.post<EgressPolicy>(
        `${ENDPOINT}/${encodedId}/egresspolicy`,
        policy
      );
      console.log('[AppService] Updated egress policy:', response.data);
      return response.data;
    } catch (error) {
      console.error('[AppService] Failed to set egress policy for app:', id, error);
      throw error;
    }
  },

  /**
   * Get egress decisions (allowed/denied requests) for an app.
   * GET /sandboxes/:id/egress-decisions
   */
  async getEgressDecisions(id: string): Promise<EgressDecisionsResponse> {
    console.log('[AppService] Fetching egress decisions for app:', id);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.get<EgressDecisionsResponse>(
        `${ENDPOINT}/${encodedId}/egress-decisions`
      );
      console.log('[AppService] Fetched egress decisions:', response.data);
      return response.data;
    } catch (error) {
      console.error('[AppService] Failed to fetch egress decisions for app:', id, error);
      throw error;
    }
  },
};
